import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Button, { ButtonVariant } from 'components/base/Button';
import { useImportarXmlNFe } from 'hooks/useNFe';
import { useEmpresas } from 'providers/EmpresaProvider';
import { useToast } from 'providers/ToastProvider';

interface BotaoImportarXmlNFeProps {
  size?: 'sm' | 'lg';
  variant?: ButtonVariant;
  className?: string;
}

const BotaoImportarXmlNFe: React.FC<BotaoImportarXmlNFeProps> = ({
  size,
  variant = 'outline-secondary',
  className = '',
}) => {
  const navigate = useNavigate();
  const { selectedIds } = useEmpresas();
  const { showToast } = useToast();
  const empresaId = selectedIds[0];
  const inputRef = useRef<HTMLInputElement>(null);

  const { mutate: importar, isPending } = useImportarXmlNFe();

  const handleClick = () => inputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!empresaId) {
      showToast('Selecione uma empresa antes de importar o XML.', 'error');
      return;
    }

    importar(
      { xml: file, empresaId },
      {
        onSuccess: (data) => {
          navigate(`/operacional/nfe/${data.emissaoId}`);
        },
        onError: (error: any) => {
          const msg =
            error?.response?.data?.message ??
            'Erro ao importar XML. Verifique se o arquivo é uma NF-e autorizada.';
          showToast(msg, 'error');
        },
      }
    );

    // reset input so same file can be re-selected
    e.target.value = '';
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".xml,application/xml,text/xml"
        className="d-none"
        onChange={handleFileChange}
      />
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        disabled={isPending || !empresaId}
        className={`d-flex align-items-center ${className}`}
      >
        {isPending ? (
          <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
        ) : (
          <FontAwesomeIcon icon={faFileImport} className="me-2" />
        )}
        Importar XML
      </Button>
    </>
  );
};

export default BotaoImportarXmlNFe;
